import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@components/index';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { addPaymentMethod } from '@api/user/apiService';
import { FormInput } from '@components/FormInput/ui/FormInput';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { CreatePaymentMethodData } from '@stripe/stripe-js';
import { COUNTRY_LIST, US_STATES_OPTIONS } from '@static/staticData';
import * as Sentry from '@sentry/react';
import useToast from '@hooks/useToast';
import { getApiErrorMessage } from '@shared/utils/handle-backend-error';
import { useCaseSubscriptionPlans } from '@shared/hooks/useCaseSubscriptionPlans';
import IconButton from '@mui/material/IconButton';
import { IconX, IconLock } from '@tabler/icons-react';
import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import Logo from '@components/common/Logo';
import styles from './AddPaymentMethodModal.module.scss';
import PlansPreviewModal from './PlansPreviewModal';

interface DropdownListItem {
  value: string;
  label: string;
}

interface AddPaymentMethodModalProps {
  showModal: boolean;
  onCancel: () => void;
  onConfirm: (
    paymentMethodId: string,
    selectedPlanPriceId?: number | null
  ) => void | Promise<void>;
  /** Optional callback when an à la carte purchase is successful directly (no card entry) */
  onAlaCartePurchaseSuccess?: () => void | Promise<void>;
  /** Optional callback when a trial is started (if the plans modal is shown) */
  onTrialStarted?: () => void | Promise<void>;
  // When true (default), this component will save the payment method directly (authenticated flow: Billing page)
  // When false, it will only return the paymentMethodId to the parent (unauthenticated flow: Sign-up)
  saveDirectly?: boolean;
  /** Whether to show the plans preview modal first. If not provided, defaults to !saveDirectly */
  initialShowPlans?: boolean;
  /** Name of the currently active subscription plan (for highlighting in upgrade flow) */
  currentPlanName?: string | null;
  /** Active case limit of the current plan (for highlighting in upgrade flow) */
}

interface PaymentMethodForm {
  name: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

interface PaymentMethodFormErrors {
  card_expiry: string;
  card_number: string;
  card_cvc: string;
}

const COUNTRY_LABELS_LIST = COUNTRY_LIST.map(
  (item: DropdownListItem) => item.label
);

const US_STATES_LABELS_LIST = US_STATES_OPTIONS.map(
  (item: DropdownListItem) => item.label
);

const initialState = {
  errors: {
    card_expiry: '',
    card_number: '',
    card_cvc: '',
  },
};

export const demandWithAudioFilesFormValidationSchema = object().shape({
  name: string().required('Please fill the cardholder name'),
  address_line1: string().required('Please fill the address line 1'),
  address_line2: string(),
  city: string().required('Please fill the city'),
  state: string()
    .oneOf(US_STATES_LABELS_LIST)
    .required('Please fill the state'),
  postal_code: string().required('Please fill the postal code'),
  country: string()
    .oneOf(COUNTRY_LABELS_LIST)
    .required('Please fill the country'),
});

const defaultValues = {
  name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'United States',
};

function AddPaymentMethodModal(props: AddPaymentMethodModalProps) {
  const {
    showModal,
    onCancel,
    onConfirm,
    onAlaCartePurchaseSuccess,
    onTrialStarted,
    saveDirectly = true,
    initialShowPlans,
    currentPlanName,
  } = props;
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { trialPlan } = useCaseSubscriptionPlans();
  console.log('trialPlan', trialPlan);
  const trialDurationDays = trialPlan?.trial_duration_days ?? 30;
  const trialCasesLimit = trialPlan?.active_case_limit ?? 3;

  const formMethods = useForm<PaymentMethodForm>({
    defaultValues,
    resolver: yupResolver(demandWithAudioFilesFormValidationSchema as any),
    mode: 'onBlur',
  });
  const [errors, setErrors] = useState<PaymentMethodFormErrors>(
    initialState.errors
  );
  const stripe = useStripe();
  const elements = useElements();

  const [isSubmitting, setIsSubmitting] = useState(false);
  // In sign-up flow (saveDirectly=false), show plans first, then collect payment details
  const [showPlansModal, setShowPlansModal] = useState(
    initialShowPlans ?? !saveDirectly
  );
  const [checkoutType, setCheckoutType] = useState<
    'trial' | 'plan' | 'a-la-carte' | null
  >(null);
  const [selectedPlanPriceId, setSelectedPlanPriceId] = useState<number | null>(
    null
  );
  const [alaCartePaymentIntent, setAlaCartePaymentIntent] = useState<{
    client_secret: string;
    payment_intent_id: string;
    items: any[];
  } | null>(null);
  // Authenticated flow only: the card this dialog has already attached to the
  // customer. Set once the save succeeds, so a retry after a failed upgrade or
  // trial start reuses it instead of tokenising and attaching the same card
  // again (ZLAYT-1362).
  const [savedPaymentMethodId, setSavedPaymentMethodId] = useState<
    string | null
  >(null);
  const prevShowModal = useRef<boolean>(false);
  const bodyScrollLockRef = useRef<{
    locked: boolean;
    originalOverflow: string;
  }>({ locked: false, originalOverflow: '' });

  const isFormValid = () => {
    return formMethods.formState.isValid;
  };

  /**
   * Lock background scroll only when the *payment details* modal is open.
   *
   * Important: the plans picker uses MUI `Dialog` (`PlansPreviewModal`), which already
   * manages body scroll locking internally. If we also lock at the same time, we can
   * accidentally "remember" `overflow: hidden` as the original value and keep the page
   * non-scrollable even after closing.
   */
  useEffect(() => {
    const shouldLockBodyScroll = Boolean(showModal && !showPlansModal);

    if (shouldLockBodyScroll && !bodyScrollLockRef.current.locked) {
      bodyScrollLockRef.current.originalOverflow =
        document.body.style.overflow || '';
      document.body.style.overflow = 'hidden';
      bodyScrollLockRef.current.locked = true;
      return;
    }

    if (!shouldLockBodyScroll && bodyScrollLockRef.current.locked) {
      document.body.style.overflow =
        bodyScrollLockRef.current.originalOverflow || '';
      bodyScrollLockRef.current.locked = false;
      bodyScrollLockRef.current.originalOverflow = '';
    }
  }, [showModal, showPlansModal]);

  // Safety: ensure we always unlock if this component unmounts while locked.
  useEffect(() => {
    return () => {
      if (bodyScrollLockRef.current.locked) {
        document.body.style.overflow =
          bodyScrollLockRef.current.originalOverflow || '';
      }
      bodyScrollLockRef.current.locked = false;
      bodyScrollLockRef.current.originalOverflow = '';
    };
  }, []);

  // Reset state each time the modal is opened to avoid stale trial/plan state
  useEffect(() => {
    if (showModal && !prevShowModal.current) {
      setCheckoutType(null);
      setSelectedPlanPriceId(null);
      setAlaCartePaymentIntent(null);
      setSavedPaymentMethodId(null);
      setShowPlansModal(initialShowPlans ?? !saveDirectly);
      console.log(
        '[AddPaymentMethodModal] Modal opened, reset state. saveDirectly:',
        saveDirectly,
        'showPlansModal:',
        initialShowPlans ?? !saveDirectly
      );
    }
    prevShowModal.current = showModal;
  }, [showModal, saveDirectly, initialShowPlans]);

  // Debug: Log when payment modal should appear
  useEffect(() => {
    console.log(
      '[AddPaymentMethodModal] Render state - showModal:',
      showModal,
      'showPlansModal:',
      showPlansModal,
      'shouldShowPaymentModal:',
      !showPlansModal && showModal
    );
  }, [showModal, showPlansModal]);

  /**
   * Authenticated flow (Billing page, Subscription upgrade dialog): attach the
   * card to the customer, then let the parent finish what the card was
   * collected for (nothing, a trial, a plan upgrade).
   *
   * Runs under the submit spinner from start to finish, so a slow upgrade
   * cannot be submitted twice, and a rejection reaches the caller's catch with
   * the dialog still open. The old handler judged success by a
   * `message === "Success"` envelope that the current endpoint never sends, so
   * every successful save raised "Something went wrong" (ZLAYT-1362).
   */
  const savePaymentMethodAndConfirm = async (paymentMethodId: string) => {
    await addPaymentMethod(paymentMethodId);
    setSavedPaymentMethodId(paymentMethodId);
    if (!checkoutType) {
      // Plain "add a card" from Billing. During a checkout the parent
      // announces the outcome that matters (trial started, plan upgraded).
      showSuccess(
        'Success',
        'Payment method added successfully. You can now use it for your subscription.'
      );
    }
    // Pass selectedPlanPriceId through without coalescing:
    // - null: a-la-carte
    // - -1: free trial sentinel (signup flow)
    // - number: paid plan price id
    // - undefined: no selection
    await onConfirm(paymentMethodId, selectedPlanPriceId);
  };

  // The saved card is only reused while the card fields still hold it. Leaving
  // for the plans picker unmounts them (and may switch to an a la carte
  // purchase, which must pay through its own intent), so forget it then.
  useEffect(() => {
    if (showPlansModal) {
      setSavedPaymentMethodId(null);
    }
  }, [showPlansModal]);

  const handleChange = (
    event: any,
    field: keyof PaymentMethodFormErrors
  ): void => {
    // Only the Stripe card fields report here. A different card typed in after
    // a failed upgrade must be tokenised and saved, not swapped for the old one.
    setSavedPaymentMethodId(null);
    setErrors((prevState) => {
      return {
        ...prevState,
        [field]: event.error ? event.error.message : '',
      };
    });
  };

  const handleSubmit = async (data: PaymentMethodForm) => {
    if (saveDirectly && savedPaymentMethodId && !alaCartePaymentIntent) {
      // The card from the previous attempt is already on the account; only
      // the parent's step (the upgrade or trial start) failed. Retry that step
      // rather than tokenising and attaching the same card a second time,
      // which is what surfaced "This card is already added to your account"
      // to QA on ZLAYT-1362.
      try {
        setIsSubmitting(true);
        await onConfirm(savedPaymentMethodId, selectedPlanPriceId);
      } catch (error: any) {
        Sentry.captureException(error);
        showError(
          'Payment Error',
          getApiErrorMessage(error, 'An error occurred. Please try again.')
        );
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (elements && stripe) {
      const cardNumberElement = elements.getElement(CardNumberElement);
      const cardExpiryElement = elements.getElement(CardExpiryElement);
      const cardCvcElement = elements.getElement(CardCvcElement);

      if (cardNumberElement && cardExpiryElement && cardCvcElement) {
        const country = COUNTRY_LIST.find(
          (item: DropdownListItem) => item.label === data.country
        );

        const state = US_STATES_OPTIONS.find(
          (item: DropdownListItem) => item.label === data.state
        );

        const paymentMethodData: CreatePaymentMethodData = {
          type: 'card',
          card: cardNumberElement,
          billing_details: {
            name: data.name,
            address: {
              line1: data.address_line1,
              line2: data.address_line2,
              city: data.city,
              state: state?.value,
              postal_code: data.postal_code,
              country: country?.value,
            },
          },
        };

        try {
          setIsSubmitting(true);

          const { paymentMethod, error } =
            await stripe.createPaymentMethod(paymentMethodData);

          if (error || !paymentMethod) {
            if (error?.type === 'validation_error') {
              setIsSubmitting(false);
              return;
            }

            const errorMessage =
              error?.message ??
              'Something went wrong during payment method creation';

            throw new Error(errorMessage);
          }

          // Handle à la carte Payment Intent confirmation
          if (alaCartePaymentIntent) {
            try {
              // 1. If in sign-up flow, save the payment method first to avoid "used" error
              if (!saveDirectly) {
                console.log(
                  '[AddPaymentMethodModal] Sign-up flow à la carte: Saving PM first via onConfirm'
                );
                await onConfirm(paymentMethod.id, null);
                console.log(
                  '[AddPaymentMethodModal] PM saved successfully, now confirming payment'
                );
              }

              // 2. Confirm Payment Intent with payment method
              const { error: confirmError, paymentIntent } =
                await stripe.confirmCardPayment(
                  alaCartePaymentIntent.client_secret,
                  {
                    payment_method: paymentMethod.id,
                  }
                );

              if (confirmError) {
                throw new Error(confirmError.message || 'Payment failed');
              }

              if (paymentIntent?.status === 'succeeded') {
                // 3. Payment successful, process purchase records on backend
                const { AlaCarteApi } = await import(
                  '@api/alacarte/alaCarteApi'
                );
                await AlaCarteApi.purchase(
                  alaCartePaymentIntent.items,
                  alaCartePaymentIntent.payment_intent_id
                );

                const successText =
                  'Your purchase was completed and credits were added to your account. Please log in to start using Zlayt AI.';
                showSuccess('Success', successText, 10000);

                // 4. Handle final redirect or modal close
                if (!saveDirectly) {
                  // Sign-up flow: purchase is complete, redirect to sign-in
                  console.log(
                    '[AddPaymentMethodModal] Purchase complete, redirecting to sign-in'
                  );
                  setTimeout(() => {
                    navigate('/sign-in');
                  }, 1500);
                } else {
                  // Authenticated flow (Billing page): Notify parent to refresh and close
                  await onConfirm(paymentMethod.id, null);
                }
                setIsSubmitting(false);
              } else {
                throw new Error('Payment was not completed');
              }
            } catch (error: any) {
              console.error(
                '[AddPaymentMethodModal] À la carte purchase error:',
                error
              );
              Sentry.captureException(error);
              const errorMessage = getApiErrorMessage(
                error,
                error.message ?? 'Failed to complete payment'
              );
              showError('Payment Error', errorMessage);
              setIsSubmitting(false);
            }
          } else if (saveDirectly) {
            // Authenticated flow (Billing page, Subscription upgrade dialog):
            // save the card, then the parent completes the checkout. Any
            // rejection lands in the catch below, with the dialog still open.
            await savePaymentMethodAndConfirm(paymentMethod.id);
            setIsSubmitting(false);
          } else {
            // Unauthenticated flow (Sign-up) -> parent handles saving.
            // Await it. The parent's promise rejects when saving the payment
            // method or creating the subscription fails, and without awaiting
            // that rejection went unhandled: the modal reported nothing and the
            // user was left looking at the signup form with no explanation.
            // Awaiting lets the catch below render the reason in place. ZLA-010.
            await onConfirm(paymentMethod.id, selectedPlanPriceId);
            setIsSubmitting(false);
          }
        } catch (error: any) {
          Sentry.captureException(error);
          // Stripe client-side errors carry the reason on error.message; API
          // rejections (the card save, or the parent's upgrade or trial start)
          // carry it in error.response.data. Either way it is shown here, in
          // the dialog, which stays open.
          showError(
            'Payment Error',
            getApiErrorMessage(error, 'An error occurred. Please try again.')
          );
          setIsSubmitting(false);
        }
      }
    }
  };

  return (
    <>
      {/* Payment method form modal - shows when plans modal is closed */}
      {!showPlansModal && showModal && (
        <Modal
          showModal={showModal}
          modalClassname={styles.modalOverlayFix}
          modalBaseClassname={styles.modalBaseFix}
        >
          <div className={styles.layout}>
            <div className={styles.header}>
              <div className={styles.headerTop}>
                <Logo />
                <IconButton onClick={onCancel} className={styles.closeBtn}>
                  <IconX />
                </IconButton>
              </div>
              <div className={styles.headerContent}>
                <p className={styles.title}>Payment Information</p>
                {/* Adding a card from Billing is not a plan purchase (ZLAYT-1252) */}
                {saveDirectly && !checkoutType ? (
                  <p className={styles.subtitle}>
                    Add a payment method to your account
                  </p>
                ) : (
                  <p className={styles.subtitle}>
                    Complete your subscription setup
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setShowPlansModal(true)}
                      sx={{ ml: 1, textTransform: 'none', p: 0 }}
                    >
                      Upgrade Plan
                    </Button>
                  </p>
                )}

                {checkoutType === 'trial' && (
                  <div className={styles.trialCard}>
                    <div className={styles.trialIcon}>
                      <IconLock size={18} />
                    </div>
                    <div className={styles.trialBody}>
                      <div className={styles.trialHeading}>
                        <span className={styles.trialTitle}>
                          {trialDurationDays}-Day Free Trial
                        </span>
                        <span className={styles.trialBadge}>No charge</span>
                      </div>
                      <ul className={styles.trialList}>
                        <li>
                          Try Zlayt AI with {trialCasesLimit} complimentary
                          cases
                        </li>
                        <li>
                          Your card is only charged after the{' '}
                          {trialDurationDays}-day trial ends
                        </li>
                        <li>
                          Continues with our Tier 1 plan after the{' '}
                          {trialDurationDays}
                          -day trial
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <form
              onSubmit={formMethods.handleSubmit(handleSubmit)}
              className={styles.form}
            >
              <div className={styles.formRow}>
                <div className={styles.bigBlock}>
                  <label className={styles.label}>
                    Cardholder Name <span>*</span>
                  </label>
                  <FormInput
                    bordered
                    type="input"
                    placeholder="Enter cardholder name"
                    className={styles.inputWide}
                    inputWrapperClassName={styles.elementBlock}
                    {...formMethods.register('name')}
                  />
                </div>
                <div className={styles.smallBlock}>
                  <label>
                    Expiry Date <span>*</span>
                  </label>
                  <div className={styles.elementBlock}>
                    <CardExpiryElement
                      onChange={(event) => handleChange(event, 'card_expiry')}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.bigBlock}>
                  <label>
                    Card Number <span>*</span>
                  </label>
                  <div className={styles.elementBlock}>
                    <CardNumberElement
                      onChange={(event) => handleChange(event, 'card_number')}
                    />
                  </div>
                </div>
                <div className={styles.smallBlock}>
                  <label>
                    CVC <span>*</span>
                  </label>
                  <div className={styles.elementBlock}>
                    <CardCvcElement
                      onChange={(event) => handleChange(event, 'card_cvc')}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.halfBlock}>
                  <label>
                    Address Line 1 <span>*</span>
                  </label>
                  <FormInput
                    bordered
                    type="input"
                    placeholder="Enter address line 1"
                    className={styles.inputWide}
                    inputWrapperClassName={styles.elementBlock}
                    {...formMethods.register('address_line1')}
                  />
                </div>
                <div className={styles.halfBlock}>
                  <label>Address Line 2</label>
                  <FormInput
                    bordered
                    type="input"
                    placeholder="Enter address line 2"
                    className={styles.inputWide}
                    inputWrapperClassName={styles.elementBlock}
                    {...formMethods.register('address_line2')}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.halfBlock}>
                  <label>
                    City <span>*</span>
                  </label>
                  <FormInput
                    bordered
                    type="input"
                    placeholder="Enter city"
                    className={styles.inputWide}
                    inputWrapperClassName={styles.elementBlock}
                    {...formMethods.register('city')}
                  />
                </div>
                <div className={styles.halfBlock}>
                  <label>
                    State <span>*</span>
                  </label>
                  <FormInput
                    bordered
                    control={formMethods.control}
                    type="inputSelect"
                    placeholder="Choose state"
                    values={US_STATES_LABELS_LIST}
                    className={styles.inputWide}
                    inputWrapperClassName={styles.elementBlock}
                    {...formMethods.register('state')}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.halfBlock}>
                  <label>
                    Country <span>*</span>
                  </label>
                  <FormInput
                    required
                    bordered
                    control={formMethods.control}
                    type="inputSelect"
                    placeholder="Choose country"
                    values={COUNTRY_LABELS_LIST}
                    className={styles.inputWide}
                    inputWrapperClassName={styles.elementBlock}
                    {...formMethods.register('country')}
                  />
                </div>
                <div className={styles.halfBlock}>
                  <label>
                    Postal Code <span>*</span>
                  </label>
                  <FormInput
                    bordered
                    type="input"
                    placeholder="Enter postal code"
                    className={styles.inputWide}
                    inputWrapperClassName={styles.elementBlock}
                    {...formMethods.register('postal_code')}
                  />
                </div>
              </div>
              <div className={styles.formErrors}>
                <p>
                  {Object.keys(errors)
                    .filter(
                      (key) =>
                        errors[key as keyof PaymentMethodFormErrors] !== ''
                    )
                    .map((key) => (
                      <>
                        {errors[key as keyof PaymentMethodFormErrors]}
                        <br />
                      </>
                    ))}
                </p>
              </div>
              <div className={styles.actions}>
                <Button
                  className={styles.cancelBtn}
                  variant="outlined"
                  onClick={onCancel}
                >
                  {saveDirectly ? 'Cancel' : 'Back to Registration'}
                </Button>
                <LoadingButton
                  className={styles.saveBtn}
                  type="submit"
                  variant="contained"
                  disabled={!isFormValid()}
                  loading={isSubmitting}
                >
                  {saveDirectly && !checkoutType
                    ? 'Add Payment Method'
                    : checkoutType === 'a-la-carte'
                      ? 'Complete Purchase'
                      : checkoutType === 'plan'
                        ? 'Start Subscription'
                        : `Start ${trialDurationDays}-Day Free Trial`}
                </LoadingButton>
              </div>
            </form>
          </div>
        </Modal>
      )}
      <PlansPreviewModal
        showModal={showModal && showPlansModal}
        onClose={onCancel}
        onPlanSelected={async (planPriceId) => {
          // After picking a plan, proceed to payment details
          // -1 is the sentinel for trial (when user has no payment method and needs to add one first)
          if (planPriceId === -1) {
            setCheckoutType('trial');
          } else {
            setCheckoutType('plan');
          }
          setSelectedPlanPriceId(planPriceId);
          setShowPlansModal(false);
        }}
        onTrialStarted={async () => {
          // If parent provided onTrialStarted, call it (e.g. to start trial immediately)
          if (onTrialStarted) {
            await onTrialStarted();
            return;
          }
          // Default: proceed to payment details (collect card for trial)
          setCheckoutType('trial');
          // Use sentinel -1 so parent can distinguish trial vs a-la-carte (null)
          setSelectedPlanPriceId(-1);
          setShowPlansModal(false);
        }}
        onAlaCartePurchaseSuccess={onAlaCartePurchaseSuccess}
        currentPlanName={currentPlanName}
        onAlaCarteSelected={async (paymentIntentData) => {
          // After selecting à la carte items, proceed to payment details
          console.log(
            '[AddPaymentMethodModal] onAlaCarteSelected called with:',
            paymentIntentData
          );
          console.log(
            '[AddPaymentMethodModal] Current state - showModal:',
            showModal,
            'showPlansModal:',
            showPlansModal
          );
          setCheckoutType('a-la-carte');
          setAlaCartePaymentIntent(paymentIntentData);
          setShowPlansModal(false);
          console.log(
            '[AddPaymentMethodModal] Set showPlansModal to false, payment modal should now appear'
          );
          // Payment modal should now appear because !showPlansModal && showModal will be true
        }}
      />
    </>
  );
}

export default AddPaymentMethodModal;
